// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The aiming reticle for mixed-reality placement, in the browser (M9-53; 06 §3).
//
// **The ritual computed where the reticle should be, the report carried it, and no scene drew
// it.** `RitualView.reticle` has said "where to draw the reticle" since M5, and a comment in
// `XRRuntime` explains why "the player sees a reticle for a moment even when the machine is about
// to come back on its own" — a sentence about a picture that has never existed in this client. The
// player aimed by toast: *"Look where you want the machine and pinch"*, over a room with nothing
// in it to look at. The native client has drawn its reticle since its placement ritual existed;
// this is the browser twin of that visual.
//
// **Generated in code, like every asset in the product**: a radial-gradient ring on a canvas,
// the same technique as `lightSpill.ts` one file over, because a crisp `RingGeometry` edge
// shimmers against a passthrough camera image and a soft one reads as light on the floor.
//
// **Additive, no depth write, and slightly lifted.** The reticle sits on real geometry the
// runtime reported this frame — a floor, a table — and a z-fighting reticle flickers exactly when
// the player is holding still to aim, which is the moment it most needs to look stable.

import {
  AdditiveBlending,
  CanvasTexture,
  Group,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
} from 'three'

/** Where the ritual says to draw, `RitualView.reticle`'s own shape. */
export interface ReticleHit {
  readonly x: number
  readonly y: number
  readonly z: number
  readonly yaw?: number
}

export interface ReticleBuild {
  readonly group: Group
  /**
   * Point the reticle at this frame's surface, or hide it.
   *
   * `undefined` is most frames: every phase but `aiming`, and the aiming frames where the room
   * has not answered yet — the state `advanceRitual` explains a player cannot tell from an empty
   * room, which is why the toast handles it and this does not try to.
   */
  aimAt(hit: ReticleHit | undefined, seconds: number): void
  dispose(): void
}

/** Across the whole visual, metres. Sized to read as "the machine goes here", not as a cursor. */
const RETICLE_ACROSS = 0.5

/** Lift off the reported surface, metres. Above the pool of light's 0.005, below perception. */
const LIFT = 0.006

/** How much of the radius the bright ring occupies, and where it sits. */
const RING_AT = 0.72
const RING_WIDTH = 0.1

/**
 * One canvas, drawn once: a soft outer glow, a bright ring, and a dim centre dot.
 *
 * Drawn in greys and coloured by the material, so a later "make it match the room" pass has one
 * knob rather than a repaint.
 */
function paint(): HTMLCanvasElement | undefined {
  const side = 256
  // The `noticePanel` pattern: reach for the document only if the host has one, so the geometry
  // half of this file is testable where there is no DOM and no 2-D context.
  const host = (globalThis as { document?: { createElement: (tag: string) => HTMLCanvasElement } })
    .document
  const canvas = host?.createElement('canvas')
  if (canvas === undefined) return undefined
  canvas.width = side
  canvas.height = side
  const ctx = canvas.getContext('2d')
  if (ctx === null) return canvas
  const half = side / 2
  const glow = ctx.createRadialGradient(half, half, 0, half, half, half)
  glow.addColorStop(0, 'rgba(255,255,255,0.10)')
  glow.addColorStop(RING_AT - RING_WIDTH, 'rgba(255,255,255,0.06)')
  glow.addColorStop(RING_AT, 'rgba(255,255,255,0.85)')
  glow.addColorStop(Math.min(1, RING_AT + RING_WIDTH), 'rgba(255,255,255,0.06)')
  glow.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = glow
  ctx.fillRect(0, 0, side, side)
  // The centre dot: where the machine's centre will stand, which is what `place()` commits.
  ctx.fillStyle = 'rgba(255,255,255,0.9)'
  ctx.beginPath()
  ctx.arc(half, half, side * 0.02, 0, Math.PI * 2)
  ctx.fill()
  return canvas
}

export function buildReticle(): ReticleBuild {
  const group = new Group()
  group.name = 'placement-reticle'
  group.visible = false

  const painted = paint()
  const texture = painted === undefined ? undefined : new CanvasTexture(painted)
  if (texture !== undefined) {
    texture.colorSpace = SRGBColorSpace
    texture.minFilter = LinearFilter
    texture.magFilter = LinearFilter
  }

  const material = new MeshBasicMaterial({
    ...(texture === undefined ? {} : { map: texture }),
    color: 0x9fd8ff,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
  })
  const geometry = new PlaneGeometry(RETICLE_ACROSS, RETICLE_ACROSS)
  const disc = new Mesh(geometry, material)
  // Flat on the surface: the plane's +Z becomes world +Y.
  disc.rotation.x = -Math.PI / 2
  group.add(disc)

  return {
    group,
    aimAt(hit: ReticleHit | undefined, seconds: number): void {
      if (hit === undefined) {
        group.visible = false
        return
      }
      group.visible = true
      group.position.set(hit.x, hit.y + LIFT, hit.z)
      // `−yaw`: a `Placement` yaw turns the other way from Three's Euler Y — see
      // `XRRuntime.applyPlacement`. The reticle shows where the machine will stand, so it is
      // drawn with the machine's own sign or it points the wrong way on every turned surface.
      group.rotation.y = -(hit.yaw ?? 0)
      // A slow breath, so a tracked surface reads as live rather than as a decal that happens to
      // be under the gaze. ±4% at half a hertz — under the flash-safety thresholds by two orders
      // of magnitude, so `flashReduced` has nothing to say about it.
      const breath = 1 + 0.04 * Math.sin(seconds * Math.PI)
      group.scale.setScalar(breath)
    },
    dispose(): void {
      geometry.dispose()
      material.dispose()
      texture?.dispose()
    },
  }
}
